import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

const MENU_Z = 10060;

/** Matches native `<select>` lookup styling (e.g. Stop Sale Date Type field). */
const DEFAULT_LABEL_CLASS =
  'text-xs font-medium text-gray-700 dark:text-gray-300';
const DEFAULT_TRIGGER_CLASS =
  'w-full h-9 min-h-[36px] px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer flex items-center justify-between gap-2 hover:border-gray-400 dark:hover:border-gray-500 transition-colors';

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Seçiniz',
  className = '',
  label = '',
  labelClassName = DEFAULT_LABEL_CLASS,
  triggerClassName = DEFAULT_TRIGGER_CLASS,
  displayKey = 'name',
  valueKey = 'id',
  /** `single`: one selection; menu closes after pick */
  selectionMode = 'multiple',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const updateMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el || !isOpen) return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const preferredMax = 280;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    // Varsayılan: aşağı aç. Yukarı ancak altta neredeyse yer yoksa ve üstte belirgin daha fazla alan varsa.
    const minComfortableBelow = 56;
    const openUp =
      spaceBelow < minComfortableBelow &&
      spaceAbove > spaceBelow + 80 &&
      spaceAbove > 120;
    const maxHeight = Math.min(preferredMax, openUp ? spaceAbove - gap : spaceBelow - gap);
    if (openUp) {
      setMenuStyle({
        position: 'fixed',
        left: r.left,
        width: r.width,
        bottom: window.innerHeight - r.top + gap,
        maxHeight: Math.max(120, maxHeight),
        zIndex: MENU_Z,
      });
    } else {
      setMenuStyle({
        position: 'fixed',
        left: r.left,
        width: r.width,
        top: r.bottom + gap,
        maxHeight: Math.max(120, maxHeight),
        zIndex: MENU_Z,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, updateMenuPosition, options.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      const t = triggerRef.current;
      const m = menuRef.current;
      if (t?.contains(event.target) || m?.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (option, event) => {
    event.stopPropagation();
    const value = option[valueKey];
    if (selectionMode === 'single') {
      const isSame = selectedValues.some((v) => v === value || Number(v) === Number(value));
      if (isSame) {
        setIsOpen(false);
        return;
      }
      onChange([value]);
      setIsOpen(false);
      return;
    }
    const isSelected = selectedValues.some((v) => v === value || Number(v) === Number(value));
    if (isSelected) {
      onChange(selectedValues.filter((v) => v !== value && Number(v) !== Number(value)));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const getSelectedNames = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const selected = options.find(
        (opt) => opt[valueKey] === selectedValues[0] || Number(opt[valueKey]) === Number(selectedValues[0])
      );
      return selected ? selected[displayKey] : placeholder;
    }
    return `${selectedValues.length} seçili`;
  };

  const filteredOptions = options.filter((option) =>
    String(option[displayKey] ?? '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const dropdownPanel =
    isOpen &&
    menuStyle &&
    createPortal(
      <div
        id={menuId}
        ref={menuRef}
        role="listbox"
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-xl flex flex-col overflow-hidden"
        style={menuStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {options.length > 5 && (
          <div className="p-2 border-b border-gray-200 dark:border-gray-600 shrink-0">
            <input
              type="text"
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div className="overflow-y-auto flex-1 min-h-0">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              {searchTerm ? 'Sonuç bulunamadı' : 'Seçenek yok'}
            </div>
          ) : (
            filteredOptions.map((option) => {
              const val = option[valueKey];
              const isSelected = selectedValues.some((v) => v === val || Number(v) === Number(val));
              return (
                <div
                  key={`${String(val)}-${option[displayKey]}`}
                  onClick={(e) => handleSelect(option, e)}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                    isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                  }`}
                >
                  <span className="text-gray-900 dark:text-white truncate pr-2">{option[displayKey]}</span>
                  {isSelected && (
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className={labelClassName}>{label}</label>
      )}

      <div className="relative multi-select-dropdown">
        <div
          ref={triggerRef}
          onClick={() => setIsOpen(!isOpen)}
          className={triggerClassName}
        >
          <span className={selectedValues.length > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
            {getSelectedNames()}
          </span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {dropdownPanel}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
